
import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";

const Roadnetwork = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [lineLayers, setLineLayers] = useState({});
  const [legendVisible, setLegendVisible] = useState(false);
  const legendRef = useRef(null);

  const center = [-8.8742, 125.7275]; // Timor-Leste
  const INITIAL_VIEW_STATE = { zoom: 8.49 };

  // Fetch Country Boundary
  useEffect(() => {
    fetch("/DATA/OSMbased_adm0_boundary.json")
      .then((response) => response.json())
      .then((data) => setBoundaryData(data))
      .catch((error) => console.error("Error loading boundary data:", error));
  }, []);

  // Fetch and group line features by SectorialSubdivision
  useEffect(() => {
    fetch("/cityfire/infra_line.geojson")
      .then((response) => response.json())
      .then((data) => {
        const grouped = {};

        data.features.forEach((feature) => {
          const subdivision = feature.properties?.SectorialSubdivision || "Others";
          if (!grouped[subdivision]) grouped[subdivision] = [];
          grouped[subdivision].push(feature);
        });

        const layerData = {};
        for (const [subdivision, features] of Object.entries(grouped)) {
          layerData[subdivision] = {
            type: "FeatureCollection",
            features,
          };
        }

        setLineLayers(layerData);
      })
      .catch((error) => console.error("Error loading line data:", error));
  }, []);

  // Close legend when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendVisible]);

  // Color mapping based on subdivision
  const getColorForSubdivision = (subdivision) => {
    switch (subdivision) {
      case "Road_national":
        return "black";
      case "Road_district":
        return "green";
      case "Road_rural":
        return "orange";
      case "Water_canal":
        return "violet";
      case "Water_drain":
        return "blue";
      case "Powerline":
        return "black";
      case "Road_other":
        return "gray";
      case "Waterbodies":
        return "darkblue";
      default:
        return "gray";
    }
  };

  return (
    <div className="mapcontainerall">
      <MapContainer
        center={center}
        zoom={INITIAL_VIEW_STATE.zoom}
        style={{ height: "100%", width: "100%" }}
      >
        <MapHomeControl />
        <MapInfoControl text="Road network of Timor-Leste classified by road type." />
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
       

        <LayersControl position="topright">

          {/* Country Layer */}
          {boundaryData && (
            <LayersControl.Overlay name="Country" checked>
              <GeoJSON
                data={boundaryData}
                style={{ color: "blue", weight: 2, fillOpacity: 0 }}
              />
            </LayersControl.Overlay>
          )}

  {/* Dynamic Line Layers by Subdivision */}
  {lineLayers &&
    Object.entries(lineLayers)
      .filter(
        ([subdivision]) =>
          ![
            "Water_canal",
            "Water_drain",
            "Waterbodies",
            "Powerline" 
          ].includes(subdivision)
      )
      .map(([subdivision, data]) => (
        <LayersControl.Overlay key={subdivision} name={subdivision} checked>
          <GeoJSON
            data={data}
            style={() => ({
              color: getColorForSubdivision(subdivision),
              weight: 1.5,
              fillOpacity: 0.5,
            })}
            onEachFeature={(feature, layer) => {
              if (feature.properties) {
                layer.bindPopup(
                  `<b>Info</b><br>${Object.entries(feature.properties)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join("<br>")}`
                );
              }
            }}
          />
        </LayersControl.Overlay>
      ))}
</LayersControl>


        {/* Legend */}
        <MapLegendControl
          sections={[{
            shape: "square",
            items: [
              { label: "National Roads", color: "Black" },
              { label: "District Roads", color: "green" },
              { label: "Rural Roads", color: "orange" },
              { label: "Others", color: "gray" },
            ],
          }]}
        />
      </MapContainer>
    </div>
  );
};

export default Roadnetwork;

